import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AndroidIcon from "@mui/icons-material/Android";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { getFullApiUrl } from "../../config";
import { getToken } from "../../utils";
import { ExpiredPage } from "../ExpiredPage";
import { LoadingPage } from "../LoadingPage";
import { ErrorPage } from "../ErrorPage";

const AppManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    platform: "android",
    versionName: "",
    versionCode: "",
    releaseNotes: "",
    signatureFingerprint: "",
    minSupportedVersionCode: "",
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      const token = getToken();
      setLoading(true);

      const response = await axios.get(
        getFullApiUrl("/api/v1/app/versions"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setVersions(response.data.versions || []);
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        setExpired(true);
      } else {
        console.error("Error fetching versions:", err);
        setError("Failed to load versions. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith(".apk")) {
      setUploadFile(file);
    } else {
      showSnackbar("Please select a valid APK file", "error");
    }
  };

  const handleUploadAPK = async () => {
    if (!uploadFile || !formData.versionName) {
      showSnackbar("Please select an APK file and enter version name", "error");
      return;
    }

    try {
      setUploading(true);
      const token = getToken();
      const formDataToSend = new FormData();
      formDataToSend.append("apk", uploadFile);
      formDataToSend.append("version_name", formData.versionName);

      const uploadResponse = await axios.post(
        getFullApiUrl("/api/v1/app/upload-apk"),
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      // After successful upload, create version entry
      const versionData = {
        platform: formData.platform,
        version_name: formData.versionName,
        version_code: parseInt(formData.versionCode),
        download_url: uploadResponse.data.download_url,
        release_notes: formData.releaseNotes,
        signature_fingerprint: formData.signatureFingerprint,
        min_supported_version_code: formData.minSupportedVersionCode
          ? parseInt(formData.minSupportedVersionCode)
          : 0,
      };

      await axios.post(
        getFullApiUrl("/api/v1/app/version"),
        versionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showSnackbar("Version uploaded successfully!", "success");
      setUploadDialogOpen(false);
      resetForm();
      fetchVersions();
    } catch (err) {
      console.error("Error uploading APK:", err);
      showSnackbar(
        err.response?.data?.error || "Failed to upload APK",
        "error"
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleToggleActive = async (versionId, currentStatus) => {
    try {
      const token = getToken();
      await axios.put(
        getFullApiUrl(`/api/v1/app/version/${versionId}/active`),
        { is_active: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showSnackbar("Version status updated successfully!", "success");
      fetchVersions();
    } catch (err) {
      console.error("Error toggling version status:", err);
      showSnackbar("Failed to update version status", "error");
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm("Are you sure you want to delete this version?")) {
      return;
    }

    try {
      const token = getToken();
      await axios.delete(
        getFullApiUrl(`/api/v1/app/version/${versionId}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showSnackbar("Version deleted successfully!", "success");
      fetchVersions();
    } catch (err) {
      console.error("Error deleting version:", err);
      showSnackbar("Failed to delete version", "error");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar("Copied to clipboard!", "success");
  };

  const resetForm = () => {
    setUploadFile(null);
    setFormData({
      platform: "android",
      versionName: "",
      versionCode: "",
      releaseNotes: "",
      signatureFingerprint: "",
      minSupportedVersionCode: "",
    });
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => {
      setSnackbar({ ...snackbar, open: false });
    }, 3000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (expired) {
    return <ExpiredPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  const androidVersions = versions.filter((v) => v.platform === "android");
  const activeAndroidVersion = androidVersions.find((v) => v.is_active);

  return (
    <Container component="main" maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          应用版本管理
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          管理Android应用的版本发布和更新
        </Typography>
      </Box>

      {/* Snackbar */}
      {snackbar.open && (
        <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
          {snackbar.message}
        </Alert>
      )}

      {/* Upload Button */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          上传新版本
        </Button>
      </Box>

      {/* Current Version Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AndroidIcon sx={{ mr: 1, color: "success.main" }} />
                <Typography variant="h6">Android 当前版本</Typography>
              </Box>
              {activeAndroidVersion ? (
                <>
                  <Typography variant="h4" color="success.main">
                    v{activeAndroidVersion.version_name} (
                    {activeAndroidVersion.version_code})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    发布于: {formatDate(activeAndroidVersion.created_at)}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  未设置活动版本
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                版本总数
              </Typography>
              <Typography variant="h4" color="primary">
                {androidVersions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Android 版本
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Versions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>平台</TableCell>
              <TableCell>版本号</TableCell>
              <TableCell>版本代码</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>下载链接</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {androidVersions.map((version) => (
              <TableRow key={version.id}>
                <TableCell>
                  <AndroidIcon color="success" />
                </TableCell>
                <TableCell>
                  <Typography variant="body1" fontWeight="bold">
                    v{version.version_name}
                  </Typography>
                </TableCell>
                <TableCell>{version.version_code}</TableCell>
                <TableCell>
                  <Chip
                    label={version.is_active ? "激活" : "未激活"}
                    color={version.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="复制下载链接">
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(version.download_url)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatDate(version.created_at)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={version.is_active}
                          onChange={() =>
                            handleToggleActive(version.id, version.is_active)
                          }
                          size="small"
                        />
                      }
                      label=""
                    />
                    <Tooltip title="删除">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteVersion(version.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>上传新版本</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* File Upload */}
            <Box sx={{ mb: 3 }}>
              <input
                accept=".apk"
                style={{ display: "none" }}
                id="raised-button-file"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="raised-button-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  选择 APK 文件
                </Button>
              </label>
              {uploadFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  已选择: {uploadFile.name}
                </Typography>
              )}
            </Box>

            {/* Version Name */}
            <TextField
              label="版本名称"
              fullWidth
              margin="normal"
              value={formData.versionName}
              onChange={(e) =>
                setFormData({ ...formData, versionName: e.target.value })
              }
              placeholder="例如: 1.1"
              required
            />

            {/* Version Code */}
            <TextField
              label="版本代码"
              fullWidth
              margin="normal"
              type="number"
              value={formData.versionCode}
              onChange={(e) =>
                setFormData({ ...formData, versionCode: e.target.value })
              }
              placeholder="例如: 2"
              required
            />

            {/* Signature Fingerprint */}
            <TextField
              label="证书指纹 (SHA-256)"
              fullWidth
              margin="normal"
              value={formData.signatureFingerprint}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  signatureFingerprint: e.target.value,
                })
              }
              placeholder="AA:BB:CC:DD:..."
              helperText="用于APK签名验证"
            />

            {/* Min Supported Version Code */}
            <TextField
              label="最低支持版本代码 (可选)"
              fullWidth
              margin="normal"
              type="number"
              value={formData.minSupportedVersionCode}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  minSupportedVersionCode: e.target.value,
                })
              }
              placeholder="例如: 1"
            />

            {/* Release Notes */}
            <TextField
              label="更新说明"
              fullWidth
              margin="normal"
              multiline
              rows={6}
              value={formData.releaseNotes}
              onChange={(e) =>
                setFormData({ ...formData, releaseNotes: e.target.value })
              }
              placeholder={`## 新功能\n- 支持离线播放\n- 新增视频缓存\n\n## 改进\n- 优化播放器性能\n\n## 修复\n- 修复播放器崩溃问题`}
              helperText="支持 Markdown 格式"
            />

            {/* Upload Progress */}
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  上传中... {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            取消
          </Button>
          <Button
            onClick={handleUploadAPK}
            variant="contained"
            disabled={
              !uploadFile ||
              !formData.versionName ||
              !formData.versionCode ||
              uploading
            }
          >
            上传并发布
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppManagementPage;
